import React, { useEffect, useRef, useState } from 'react';

import PageWrapper from '../PageWrapper';
import Page from '../Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { BubbleAccordion, BubbleAlert } from '@go-client/components/BubbleUI';
import ReserveNavigation from '@go-client/components/Navigations/ReserveNavigation';
import ReserveSubNavigation from '@go-client/components/Navigations/ReserveSubNavigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropertyProfileForm from '@go-client/components/BasicForms/PropertyProfileForm';

import './ReserveProfile.scss';
import HostProfileForm from '@go-client/components/BasicForms/HostProfileForm';
import { bare } from '@go-shared/lib/property';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canManageReservePropertySettings } from '@go-shared/lib/permissions';

interface ReserveProfileParams {
  property: string;
}

interface ResConfigCategory {
  key: string;
  name: string;
}

type ReserveProfileProps = RouteComponentProps<ReserveProfileParams>;

export default function ReserveProfile(props: ReserveProfileProps) {
  const { user, refreshContext: refreshUser } = useUserInfo();
  const { activeProperty, refreshContext: refreshActiveProperty } = useActiveProperty();

  const actionsRef = useRef<HTMLDivElement>(null);
  const [actionsElem, setActionsElem] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setActionsElem(actionsRef.current);
  }, []);

  const categories: ResConfigCategory[] = [];

  if (user.host) {
    categories.push({
      key: 'host',
      name: 'Host',
    });
  }

  if (canManageReservePropertySettings(user)) {
    categories.push({
      key: 'property',
      name: 'Property',
    });
  }

  const [activeCategory, setActiveCategory] = useState<ResConfigCategory | undefined>(
    categories[0]
  );

  return (
    <PageWrapper {...props}>
      <Page className="ReservablesProfile absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu flex flex-col bg-navy-darkest shadow-md">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-turquoise-darker`}>
            <div className="grid gap-2"></div>
          </div>
          <BubbleAccordion
            variant="turquoise"
            className="flex-grow flex flex-col border-t border-turquoise-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4"></div>
          </BubbleAccordion>
        </div>
        <div className="ReservablesContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ReservablesHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ReserveNavigation active="settings" />
            </div>
            <div
              className="absolute w-full bg-blue-lightest flex border-blue-lightest -mx-5 px-5"
              style={{
                height: 'calc(40px + 0.75rem)',
                bottom: 'calc(-40px - 0.75rem)',
                borderBottomWidth: '0.75rem',
                zIndex: 10,
              }}
            >
              <ReserveSubNavigation active="profile" property={activeProperty} />
            </div>
          </div>
          <div className="ReservablesBody mx-5 content-minus-logo-minus-bar min-w-900">
            <div style={{ marginTop: 'calc(40px + 0.75rem)' }}>
              {categories.length > 0 ? (
                <div className="flex flex-col">
                  <div className="grid grid-cols-4 gap-4 items-start flex-grow">
                    <div className="bg-white shadow-md rounded overflow-hidden">
                      {categories.map(c => (
                        <div
                          key={c.key}
                          className={`list-item px-5 py-3 cursor-pointer ${
                            activeCategory && activeCategory.key === c.key ? 'active' : ''
                          }`}
                          onClick={() => setActiveCategory(c)}
                        >
                          <div className="flex items-center">
                            <div className="flex-grow min-w-0">
                              <div className="list-item-name font-semibold overflow-ellipsis">
                                {c.name}
                              </div>
                            </div>
                            <FontAwesomeIcon
                              icon="chevron-right"
                              className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {activeCategory && (
                      <div
                        className="sticky col-span-3"
                        style={{
                          top: '130px',
                          height: 'calc(100vh - 130px - 40px - 0.75rem - 2rem - 1px)',
                        }}
                      >
                        <div className="bg-white shadow-md max-h-full flex flex-col rounded overflow-hidden">
                          <div
                            className="flex-grow overflow-auto"
                            style={{ maxHeight: 'calc(100% - 50px)' }}
                          >
                            <div style={{ marginBottom: '-1px' }}>
                              {activeCategory.key === 'host' && actionsElem && (
                                <div className="p-5 grid grid-cols-1 gap-4">
                                  <HostProfileForm
                                    edit={user}
                                    actionsElem={actionsElem}
                                    onFormSubmitted={refreshUser}
                                  />
                                </div>
                              )}
                              {activeCategory.key === 'property' && actionsElem && (
                                <div className="p-5 grid grid-cols-1 gap-4">
                                  <PropertyProfileForm
                                    edit={bare(activeProperty)}
                                    actionsElem={actionsElem}
                                    onFormSubmitted={refreshActiveProperty}
                                  />
                                </div>
                              )}
                            </div>
                            <div
                              className="sticky bottom-0 p-3 bg-eggshell border-t flex"
                              ref={actionsRef}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mt-3">&nbsp;</div>
                </div>
              ) : (
                <BubbleAlert variant="banana">No property configurations to show</BubbleAlert>
              )}
            </div>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
}
